import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { controlToastState } from "risa-oasis-ui_v2";
import { reRunAuthOnFileCheck } from "../../../api/postCall/reRunAuthCheck";
import { usePayerNameContextFromSingleOrderLayout } from "../../../context/payerNameContextFromSingleOrderLayout";
import { ScreenNamesForBigQuery } from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";
import { LocalStorageKeys } from "../../../enums/localStorageKeys";
import { RootState } from "../../../redux/store/store";
import { logError } from "../../../utils/customLogger";
import { getItemFromLocalStorage } from "../../../utils/localStorageHelper";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";

import { apiWorkerManager } from "../../../workerManagers/workerManager";
interface UseAuthOnFileRefreshProps {
  orderId?: string;
  patientMrn?: string;
  drugName?: string;
}

export const useAuthOnFileRefresh = ({
  orderId,
  patientMrn,
  drugName,
}: UseAuthOnFileRefreshProps) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const lastExecutionTimeRef = useRef<number>(0);

  const { singleMedicalPaOrder } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { payerName } = usePayerNameContextFromSingleOrderLayout();

  const orgId = getOrgIdForFetchExternalWorklist();
  const selectedOrganization =
    getItemFromLocalStorage(LocalStorageKeys.HEALTHCARE_FACILITY_ID) ?? "";

  const handleAuthOnFileRefresh = async () => {
    const currentTime = Date.now();
    const timeSinceLastExecution = currentTime - lastExecutionTimeRef.current;

    // Check if 10 seconds (10000ms) have passed since last execution
    if (timeSinceLastExecution < 10000) {
      controlToastState("retry-error");
      return;
    }

    lastExecutionTimeRef.current = currentTime;
    setIsRefreshing(true);
    try {
      await Promise.race([
        reRunAuthOnFileCheck({
          patient_mrn:
            patientMrn ?? singleMedicalPaOrder?.payload?.patient_id ?? "",
          drug_name:
            drugName ?? singleMedicalPaOrder?.payload?.regimen_name ?? "",
          identifier: orderId ?? "",
          org_id: selectedOrganization ?? "",
        }),
        new Promise((resolve) => setTimeout(resolve, 2000)),
      ]);
      apiWorkerManager.enqueueAndProcess({
        patient_id: singleMedicalPaOrder?.patient_id ?? "",
        order_id: singleMedicalPaOrder?.order_id ?? "",
        org_id: orgId ?? "",
        event_name: EventsName.REFRESH_BUTTON_CLICKED + " (Auth Status Screen)",
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        additional_data: {
          screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
          source: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
          payer_name: payerName,
        },
      });
      controlToastState("auth-on-file-refreshed-success");
    } catch (error) {
      logError(error as Error);
      controlToastState("auth-on-file-refreshed-failure");
    } finally {
      setIsRefreshing(false);
    }
  };

  return {
    handleAuthOnFileRefresh,
    isRefreshing,
  };
};
